/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { propertiesPanelFeelPlaygroundService } from 'features/properties-panel-feel-playground/PropertiesPanelFeelPlaygroundService';

/** @typedef {import('../result-viewer/ResultViewerPanel').ValidationState} ValidationState */

/**
 * Evaluates a FEEL expression against a context.
 *
 * @param {Object} params
 * @param {string} params.expression - FEEL expression to evaluate.
 * @param {string} params.contextJson - Context (sample data) as JSON string.
 * @param {(state: ValidationState) => void} params.setResult - Function to update the evaluation result state.
 * @param {AbortSignal} [params.signal] - Optional AbortSignal to cancel the request if needed.
 */
export const evaluateFeelExpression = async ({ expression, contextJson, setResult, signal }) => {
  setResult({ status: 'loading' });

  try {
    const payload = { expression };

    const trimmedContext = contextJson?.trim();
    const shouldIncludeContext = trimmedContext && trimmedContext !== '{}';

    if (shouldIncludeContext) {
      payload.context = JSON.parse(trimmedContext);
    }

    const result = await propertiesPanelFeelPlaygroundService.evaluateFeelExpression(payload, signal);

    if (result.error) {
      setResult({
        status: 'error',
        error: result.error
      });

      return;
    }

    setResult({
      status: result.warnings?.length > 0 ? 'warning' : 'success',
      result: result.result,
      warnings: result.warnings
    });
  } catch (error) {
    if (error.name === 'AbortError') {
      setResult({
        status: 'idle'
      });
      return;
    }

    setResult({
      status: 'unknown',
      error: error.message
    });
  }
};
